import { notify, submitSafely, useSpotifyAccounts, type SpotifyAccount } from '@church/data'
import {
  spotifyAccountSchema,
  type SpotifyAccountFormInput,
  type SpotifyAccountInput,
} from '@church/domain'
import { faFloppyDisk, faLinkSlash, faPlus, faSpotifyBrands } from '@church/ui-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useId, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import {
  Button,
  Card,
  Chip,
  ConfirmButton,
  ErrorNote,
  Field,
  Icon,
  Input,
  ListTitle,
  PageHeader,
  Select,
  SidePanel,
  Spinner,
  listRow,
} from '../components/ui.tsx'

/**
 * ForHim's own Spotify accounts (docs/15-MUSIC-AND-AI.md): every church's
 * plan playlists live in them. Each row reads how full the account is;
 * choosing one opens its panel (take new playlists or not, its capacity,
 * Disconnect). Connect another with "+ Connect account": Spotify asks to
 * sign in as that account, then comes back here (?spotify= says how it went).
 */
export const Route = createFileRoute('/_authed/admin/spotify')({
  validateSearch: z.object({ spotify: z.enum(['connected', 'failed', 'denied']).optional() }),
  component: SpotifyAccounts,
})

const STATUS = {
  active: { label: 'Active', tone: 'success' },
  full: { label: 'Full', tone: 'warning' },
  broken: { label: 'Broken', tone: 'danger' },
} as const

const OUTCOMES = {
  connected: { title: 'Connected the Spotify account', tone: 'success' },
  denied: { title: 'Spotify wasn’t allowed, so nothing was connected', tone: 'info' },
  failed: { title: 'The Spotify account didn’t connect. Try again.', tone: 'error' },
} as const

function SpotifyAccounts() {
  const { accounts, connect } = useSpotifyAccounts()
  const search = Route.useSearch()
  const navigate = useNavigate()
  const [openId, setOpenId] = useState<string | null>(null)
  // Back from Spotify: say how it went, once, and take it out of the address.
  const told = useRef(false)
  useEffect(() => {
    if (!search.spotify || told.current) return
    told.current = true
    notify(OUTCOMES[search.spotify])
    void navigate({ to: '/admin/spotify', search: {}, replace: true })
  }, [search.spotify, navigate])

  if (accounts.isPending) return <Spinner />
  const data = accounts.data
  if (!data) return <ErrorNote error={accounts.error} />
  const open = data.accounts.find((a) => a.id === openId) ?? null

  return (
    <div className="space-y-6">
      <PageHeader visible title="Spotify" />
      <ErrorNote error={connect.error} />
      {!data.configured && (
        <p className="rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning-content">
          This server has no Spotify app credentials (SPOTIFY_CLIENT_ID, SPOTIFY_CLIENT_SECRET), so
          no account can be connected and no church can have a playlist.
        </p>
      )}
      <Card bleed>
        <ListTitle title="Accounts">
          <Button
            icon={faPlus}
            variant="secondary"
            disabled={!data.configured}
            pending={connect.isPending}
            pendingLabel="Opening Spotify…"
            onClick={() =>
              connect.mutate(undefined, { onSuccess: (url) => window.location.assign(url) })
            }
          >
            Connect account
          </Button>
        </ListTitle>
        {data.accounts.length === 0 ? (
          <p className={`${listRow} text-sm text-content-muted`}>
            No account yet. Sign in to Spotify as a ForHim account, then connect it here: plan
            playlists are made in it.
          </p>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {data.accounts.map((account) => (
              <li key={account.id}>
                <button
                  type="button"
                  onClick={() => setOpenId(account.id)}
                  className={`${listRow} flex w-full items-center gap-3 text-left hover:bg-surface-sunken`}
                >
                  <Icon icon={faSpotifyBrands} className="size-6 text-success-solid" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-base text-content-default">
                      {account.displayName ?? account.spotifyUserId}
                    </span>
                    <span className="block truncate text-sm text-content-muted tabular-nums">
                      {account.playlistCount.toLocaleString()} of{' '}
                      {account.capacity.toLocaleString()} playlists
                      {account.statusReason ? ` · ${account.statusReason}` : ''}
                    </span>
                  </span>
                  <Chip size="sm" tone={STATUS[account.status].tone}>
                    {STATUS[account.status].label}
                  </Chip>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <p className="text-sm text-content-muted">
        A new playlist goes to the active account with the most room. One that Spotify stops
        accepting is marked broken: connect it again, signed in as it. Playlists end 30 days after
        their plan, which gives their room back.
      </p>
      {open && <AccountPanel key={open.id} account={open} onClose={() => setOpenId(null)} />}
    </div>
  )
}

function AccountPanel({ account, onClose }: { account: SpotifyAccount; onClose: () => void }) {
  const { update, disconnect } = useSpotifyAccounts()
  const formId = useId()
  const broken = account.status === 'broken'
  const form = useForm<SpotifyAccountFormInput, unknown, SpotifyAccountInput>({
    resolver: zodResolver(spotifyAccountSchema),
    defaultValues: {
      status: account.status === 'full' ? 'full' : 'active',
      capacity: account.capacity,
    },
  })
  const errors = form.formState.errors
  const submit = form.handleSubmit(async (input) => {
    await update.mutateAsync({
      id: account.id,
      capacity: input.capacity,
      // A broken account mends by connecting it again, not here.
      ...(!broken && { status: input.status }),
    })
    onClose()
  })

  return (
    <SidePanel
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title={account.displayName ?? account.spotifyUserId}
      description={`Spotify user ${account.spotifyUserId}`}
      primary={
        <Button
          type="submit"
          form={formId}
          icon={faFloppyDisk}
          className="py-1.5"
          pending={update.isPending}
          pendingLabel="Saving…"
          disabled={!form.formState.isDirty}
        >
          Save
        </Button>
      }
      footer={
        <ConfirmButton
          label="Disconnect"
          icon={faLinkSlash}
          variant="quietDanger"
          className="-ml-4"
          title="Disconnect this Spotify account?"
          description="Its plans’ playlists are made again in another account the next time someone asks. The old ones stay in this Spotify account."
          confirmLabel="Disconnect"
          onConfirm={() => disconnect.mutateAsync(account.id).then(onClose)}
        />
      }
    >
      <form id={formId} onSubmit={submitSafely(submit)} className="space-y-4" noValidate>
        <ErrorNote error={update.error ?? disconnect.error} />
        <p className="text-sm text-content-muted tabular-nums">
          Holds {account.playlistCount.toLocaleString()} ForHim playlists.
        </p>
        {broken ? (
          <p className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger-content">
            {account.statusReason ?? 'Spotify no longer accepts this account’s token.'} Connect it
            again from the list, signed in to Spotify as this account.
          </p>
        ) : (
          <Field label="New playlists" error={errors.status?.message}>
            <Select {...form.register('status')}>
              <option value="active">Active: takes new playlists</option>
              <option value="full">Full: takes none</option>
            </Select>
          </Field>
        )}
        <Field
          label="Capacity"
          description="Playlists it may hold. Spotify allows about 10,000 an account."
          error={errors.capacity?.message}
        >
          <Input type="number" inputMode="numeric" min={0} {...form.register('capacity')} />
        </Field>
      </form>
    </SidePanel>
  )
}
